/**
 * Authentication Page
 * SOC 2 Controls: CC6.1, CC6.2
 * ISO 27001: A.9.4.2
 *
 * Handles sign in, sign up, password reset, and MFA verification.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { migrationPasswordSchema, getPasswordRequirements, MIGRATION_PASSWORD_POLICY } from "@/lib/password-policy";
import MFAVerification from "@/components/auth/MFAVerification";
import { Shield, Info } from "lucide-react";

// Sign-in uses relaxed validation (existing users may have weaker passwords)
const signInSchema = z.object({
  email: z.string().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  password: z.string().min(1, "Password is required").max(128, "Password too long"),
});

// Sign-up uses stronger password policy (SOC 2 compliant)
const signUpSchema = z.object({
  email: z.string().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  password: migrationPasswordSchema,
});

// Legacy alias for backward compatibility
const authSchema = signInSchema;

type AuthView = 'login' | 'mfa';

export default function Auth() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Clear any white-label branding overrides on the login page
  // so Vintage's default blue theme shows (not a client's custom colors)
  useEffect(() => {
    const root = document.documentElement;
    const brandVars = ['--primary', '--primary-hover', '--ring', '--sidebar-primary', '--sidebar-ring', '--accent', '--support-teal', '--font-family-sans'];
    brandVars.forEach(v => root.style.removeProperty(v));
    document.title = 'Vintage';
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) favicon.href = '/favicon.ico';
    // Restore on unmount handled by BrandingProvider re-applying after login
  }, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // MFA state
  const [authView, setAuthView] = useState<AuthView>('login');
  const [mfaRequired, setMfaRequired] = useState(false);

  // Password strength indicator for signup
  const [showPasswordHints, setShowPasswordHints] = useState(false);

  // Password recovery state
  const [showPasswordUpdate, setShowPasswordUpdate] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);

  // Auto-redirect if user is already authenticated (e.g., from email confirmation link)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowPasswordUpdate(true);
        return;
      }
      if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && !showPasswordUpdate) {
        navigate("/dashboard", { replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate, showPasswordUpdate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const validated = authSchema.parse({ email, password });
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email: validated.email,
        password: validated.password,
      });

      if (error) throw error;

      // onAuthStateChange listener handles MFA check and navigation
      // to avoid a race condition with duplicate getAuthenticatorAssuranceLevel calls
    } catch (err) {
      if (err instanceof Error && err.message.toLowerCase().includes("rate limit")) {
        toast({
          title: "Too many attempts",
          description: "Please wait a few minutes before trying again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Sign in failed",
          description: err instanceof Error ? err.message : "Please check your credentials and try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerified = () => {
    toast({
      title: "Welcome back!",
      description: "You've successfully signed in with MFA.",
    });
    navigate("/dashboard");
  };

  const handleMfaCancel = async () => {
    // Sign out and return to login
    await supabase.auth.signOut();
    setAuthView('login');
    setMfaRequired(false);
    setEmail("");
    setPassword("");
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both password fields match.",
        variant: "destructive",
      });
      return;
    }

    try {
      const validated = migrationPasswordSchema.parse(newPassword);
      setUpdateLoading(true);

      const { error } = await supabase.auth.updateUser({ password: validated });
      if (error) throw error;

      toast({
        title: "Password updated",
        description: "Your password has been successfully reset. You are now signed in.",
      });

      setShowPasswordUpdate(false);
      setNewPassword("");
      setConfirmPassword("");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({
          title: "Password requirements not met",
          description: err.errors[0]?.message || "Please check password requirements.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Password update failed",
          description: err instanceof Error ? err.message : "Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Use stronger password validation for new accounts (SOC 2 compliant)
      const validated = signUpSchema.parse({ email, password });
      setLoading(true);

      const { error } = await supabase.auth.signUp({
        email: validated.email,
        password: validated.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`,
        },
      });

      if (error) throw error;

      toast({
        title: "Account created!",
        description: "Please check your email to confirm your account before signing in.",
      });
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({
          title: "Password requirements not met",
          description: err.errors[0]?.message || "Please check password requirements.",
          variant: "destructive",
        });
      } else if (err instanceof Error && err.message.toLowerCase().includes("rate limit")) {
        toast({
          title: "Too many attempts",
          description: "Please wait a few minutes before trying again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Sign up failed",
          description: err instanceof Error ? err.message : "Please try again with different credentials.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const validated = z.string().email("Invalid email address").parse(resetEmail);
      setResetLoading(true);

      const { error } = await supabase.auth.resetPasswordForEmail(validated, {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) throw error;

      toast({
        title: "Password reset email sent",
        description: "Check your email for the password reset link.",
      });

      setResetDialogOpen(false);
      setResetEmail("");
    } catch (err) {
      toast({
        title: "Password reset failed",
        description: err instanceof Error ? err.message : "Please check your email and try again.",
        variant: "destructive",
      });
    } finally {
      setResetLoading(false);
    }
  };

  // Password Update View (from reset link)
  if (showPasswordUpdate) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--muted))] to-[hsl(188,72%,91%)] p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-2 flex items-center justify-center">
              <img src="/logo.png" alt="Logo" className="h-16 w-16" />
            </div>
            <CardTitle className="text-2xl font-bold text-ink">Set New Password</CardTitle>
            <CardDescription>Enter your new password below</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  <ul className="text-xs space-y-1 mt-1">
                    {getPasswordRequirements(MIGRATION_PASSWORD_POLICY).map((req, i) => (
                      <li key={i}>• {req}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
              <Button type="submit" className="w-full" disabled={updateLoading}>
                {updateLoading ? "Updating..." : "Update Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // MFA Verification View
  if (authView === 'mfa' && mfaRequired) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--muted))] to-[hsl(188,72%,91%)] p-4">
        <MFAVerification
          onVerified={handleMfaVerified}
          onCancel={handleMfaCancel}
        />
      </div>
    );
  }

  // Login/Signup View
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--muted))] to-[hsl(188,72%,91%)] p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-2 flex items-center justify-center">
            <img src="/logo.png" alt="Logo" className="h-16 w-16" />
          </div>
          <CardTitle className="text-2xl font-bold text-ink">Vintage</CardTitle>
          <CardDescription>Agentic Operating System for Family Offices</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Password</Label>
                  <Input
                    id="signin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Signing in..." : "Sign In"}
                </Button>

                <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="link" className="w-full text-sm text-muted-foreground">
                      Forgot your password?
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Reset Password</DialogTitle>
                      <DialogDescription>
                        Enter your email address and we'll send you a link to reset your password.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handlePasswordReset} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="reset-email">Email</Label>
                        <Input
                          id="reset-email"
                          type="email"
                          placeholder="you@example.com"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={resetLoading}>
                        {resetLoading ? "Sending..." : "Send Reset Link"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="signup-password">Password</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-xs text-muted-foreground"
                      onClick={() => setShowPasswordHints(!showPasswordHints)}
                    >
                      <Info className="h-3 w-3 mr-1" />
                      Requirements
                    </Button>
                  </div>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  {showPasswordHints && (
                    <Alert className="mt-2">
                      <Shield className="h-4 w-4" />
                      <AlertDescription>
                        <ul className="text-xs space-y-1 mt-1">
                          {getPasswordRequirements(MIGRATION_PASSWORD_POLICY).map((req, i) => (
                            <li key={i}>• {req}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Creating account..." : "Sign Up"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
